import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import type { User, AuthError, Factor } from '@supabase/supabase-js'
import { startSession, clearSession } from '@/hooks/useSessionTimeout'
import { captureError, addBreadcrumb, setUser as setSentryUser, clearUser as clearSentryUser } from '@/lib/sentry'

interface AdminUser {
  id: string
  email: string
  role: 'super_admin' | 'admin' | 'viewer' | 'investor'
  mfa_enabled: boolean
  created_at: string
}

interface AuthContextType {
  user: User | null
  adminUser: AdminUser | null
  isLoading: boolean
  isMfaRequired: boolean
  isMfaSetupRequired: boolean
  mfaFactors: Factor[]
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null; requiresMfa?: boolean; requiresMfaSetup?: boolean }>
  signOut: () => Promise<void>
  verifyMfa: (code: string, factorId: string) => Promise<{ error: Error | null }>
  enrollMfa: () => Promise<{ qrCode: string; secret: string; factorId: string } | { error: Error }>
  verifyMfaEnrollment: (code: string, factorId: string) => Promise<{ error: Error | null }>
  unenrollMfa: (factorId: string) => Promise<{ error: Error | null }>
  getExistingFactor: () => Promise<Factor | null>
  refreshMfaFactors: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function hasPermission(role: string | undefined, requiredRole: 'super_admin' | 'admin' | 'viewer'): boolean {
  if (!role) return false
  const hierarchy: Record<string, number> = { super_admin: 3, admin: 2, viewer: 1, investor: 0 }
  return (hierarchy[role as keyof typeof hierarchy] || 0) >= (hierarchy[requiredRole] || 0)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isMfaRequired, setIsMfaRequired] = useState(false)
  const [isMfaSetupRequired, setIsMfaSetupRequired] = useState(false)
  const [mfaFactors, setMfaFactors] = useState<Factor[]>([])

  const fetchAdminUser = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Failed to fetch admin user:', error)
        captureError(new Error('Failed to fetch admin user'), { userId, supabaseError: error.message })
        return null
      }
      return data as AdminUser
    } catch (err) {
      console.error('Failed to fetch admin user:', err)
      captureError(err instanceof Error ? err : new Error('Failed to fetch admin user'), { userId })
      return null
    }
  }, [])

  const updateLastLogin = useCallback(async (userId: string) => {
    try {
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId)
    } catch (err) {
      console.error('Failed to update last login:', err)
      captureError(err instanceof Error ? err : new Error('Failed to update last login'), { userId })
    }
  }, [])

  const logAuditEvent = useCallback(async (action: string, details: Record<string, unknown> = {}) => {
    try {
      const { error } = await supabase.rpc('log_admin_audit_event', {
        p_action: action,
        p_details: details,
        p_user_agent: navigator.userAgent,
      })
      if (error) throw error
      addBreadcrumb(`Audit: ${action}`, 'audit')
    } catch (err) {
      console.error('Failed to log audit event:', err)
      captureError(err instanceof Error ? err : new Error('Failed to log audit event'), { action })
    }
  }, [])

  const refreshMfaFactors = useCallback(async () => {
    try {
      const { data } = await supabase.auth.mfa.listFactors()
      setMfaFactors(data?.totp || [])
    } catch (err) {
      console.error('Failed to refresh MFA factors:', err)
      captureError(err instanceof Error ? err : new Error('Failed to refresh MFA factors'))
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const initAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('Session error:', sessionError)
          captureError(new Error('Session initialization error'), { message: sessionError.message })
          if (isMounted) setIsLoading(false)
          return
        }

        if (session?.user) {
          if (isMounted) setUser(session.user)

          // Check MFA status first (this determines if user can access dashboard)
          try {
            const { data: factorsData } = await supabase.auth.mfa.listFactors()
            const totpFactors = factorsData?.totp || []
            if (isMounted) setMfaFactors(totpFactors)

            const verifiedFactors = totpFactors.filter((f: Factor) => f.status === 'verified')
            const hasVerifiedMfa = verifiedFactors.length > 0

            // Check assurance level from session
            const currentLevel = (session as unknown as { aal?: string }).aal
            if (hasVerifiedMfa && currentLevel === 'aal1') {
              if (isMounted) setIsMfaRequired(true)
            }
          } catch (mfaError) {
            console.error('MFA check error:', mfaError)
            captureError(mfaError instanceof Error ? mfaError : new Error('MFA check error'))
          }

          // Fetch admin user data (non-blocking for initial render)
          fetchAdminUser(session.user.id).then((admin) => {
            if (isMounted) setAdminUser(admin)
          }).catch((err) => {
            console.error('Failed to fetch admin user:', err)
            captureError(err instanceof Error ? err : new Error('Failed to fetch admin user on init'))
          })
        }
      } catch (err) {
        console.error('Auth init error:', err)
        captureError(err instanceof Error ? err : new Error('Auth init error'))
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    initAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return

      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        setSentryUser(session.user.id)
        addBreadcrumb('Auth state: SIGNED_IN', 'auth')
        fetchAdminUser(session.user.id).then((admin) => {
          if (isMounted) setAdminUser(admin)
        })
        refreshMfaFactors()
      } else if (event === 'SIGNED_OUT') {
        clearSentryUser()
        addBreadcrumb('Auth state: SIGNED_OUT', 'auth')
        setUser(null)
        setAdminUser(null)
        setIsMfaRequired(false)
        setIsMfaSetupRequired(false)
        setMfaFactors([])
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Handle token refresh - update user but don't refetch everything
        setUser(session.user)
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [fetchAdminUser, refreshMfaFactors])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) return { error }

    if (data.user) {
      setUser(data.user)
      const admin = await fetchAdminUser(data.user.id)
      setAdminUser(admin)

      // Check MFA factors
      const { data: factorsData } = await supabase.auth.mfa.listFactors()
      const totpFactors = factorsData?.totp || []
      setMfaFactors(totpFactors)

      const verifiedFactors = totpFactors.filter((f: Factor) => f.status === 'verified')

      if (verifiedFactors.length > 0) {
        // Has MFA — needs to verify
        setIsMfaRequired(true)
        return { error: null, requiresMfa: true }
      }

      // No MFA — log in directly
      await logAuditEvent('login_success', { email })
      await updateLastLogin(data.user.id)
      startSession()
      addBreadcrumb('User signed in (no MFA)', 'auth')
      setSentryUser(data.user.id)
    }

    return { error: null }
  }

  const signOut = async () => {
    await logAuditEvent('logout', {})
    clearSession()
    addBreadcrumb('User signed out', 'auth')
    clearSentryUser()
    await supabase.auth.signOut()
    setUser(null)
    setAdminUser(null)
    setIsMfaRequired(false)
    setIsMfaSetupRequired(false)
    setMfaFactors([])
  }

  const getExistingFactor = async (): Promise<Factor | null> => {
    const { data } = await supabase.auth.mfa.listFactors()
    const totpFactors = data?.totp || []
    return totpFactors.length > 0 ? totpFactors[0] : null
  }

  const enrollMfa = async () => {
    try {
      // Clean up only unverified factors to avoid race-condition buildup
      const { data: factorsData } = await supabase.auth.mfa.listFactors()
      const unverifiedFactors = (factorsData?.totp || []).filter(
        (f: Factor) => f.status === 'unverified'
      )

      for (const factor of unverifiedFactors) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: factor.id })
        } catch {
          // Continue — unenroll may fail for various reasons
        }
      }

      // Enroll a fresh TOTP factor
      let data, error
      ;({ data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `dextoro-${Date.now()}`,
      }))

      if (error) {
        // Fallback: try without friendly name in case name collision is the issue
        ;({ data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
        }))
      }

      if (error) {
        return { error }
      }

      if (!data) {
        return { error: new Error('No data returned from MFA enrollment') }
      }

      await refreshMfaFactors()

      return {
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        factorId: data.id,
      }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const unenrollMfa = async (factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId })
      
      if (error) {
        return { error }
      }

      if (user) {
        await supabase
          .from('admin_users')
          .update({ mfa_enabled: false })
          .eq('id', user.id)
        
        setAdminUser(prev => prev ? { ...prev, mfa_enabled: false } : null)
        await logAuditEvent('mfa_disabled', {})
      }

      await refreshMfaFactors()
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const verifyMfaEnrollment = async (code: string, factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code,
      })

      if (error) {
        return { error }
      }

      if (user) {
        await supabase
          .from('admin_users')
          .update({ mfa_enabled: true })
          .eq('id', user.id)

        setAdminUser(prev => prev ? { ...prev, mfa_enabled: true } : null)
        await logAuditEvent('mfa_setup_complete', {})
      }

      startSession()
      setIsMfaSetupRequired(false)
      await refreshMfaFactors()
      return { error: null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  const verifyMfa = async (code: string, factorId: string) => {
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      })

      if (challengeError) {
        return { error: challengeError }
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      })

      if (verifyError) {
        return { error: verifyError }
      }

      setIsMfaRequired(false)
      startSession()
      await logAuditEvent('login_success', {})
      if (user) await updateLastLogin(user.id)
      addBreadcrumb('User verified MFA', 'auth')
      if (user) setSentryUser(user.id)
      return { error: null }
    } catch (err) {
      captureError(err instanceof Error ? err : new Error('MFA verification failed'))
      return { error: err as Error }
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        adminUser,
        isLoading,
        isMfaRequired,
        isMfaSetupRequired,
        mfaFactors,
        signIn,
        signOut,
        verifyMfa,
        enrollMfa,
        verifyMfaEnrollment,
        unenrollMfa,
        getExistingFactor,
        refreshMfaFactors,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
